import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { shelfShape, userIdentityShape, routerShape } from '@bibliocommons/bc-prop-types';
import { withRouter } from '@bibliocommons/utils-routing';
import { bindAll } from '@bibliocommons/utils-react';

import { BackToLinkManager } from 'app/components/BackToLinkManager';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { selectCurrentUser, selectCurrentAccount } from 'app/selectors/AuthSelector';
import { selectCommentsByUsers, selectCommentsTransactions } from 'app/selectors/ugc/CommentsSelector';
import { selectQuotationsByUsers, selectQuotationsTransactions } from 'app/selectors/ugc/QuotationsSelector';
import { selectRatingsByUsers } from 'app/selectors/ugc/RatingsSelector';
import { selectTagsByUsers, selectTagsTransactions } from 'app/selectors/ugc/TagsSelector';
import { selectVideosByUsers, selectVideosTransactions } from 'app/selectors/ugc/VideosSelector';
import { selectIOwnThisByUsers, selectIOwnThisTransactions } from 'app/selectors/ugc/IOwnThisSelector';
import { selectSimilarTitlesByUsers, selectSimilarTitlesTransactions } from 'app/selectors/ugc/SimilarTitlesSelector';
import { selectPrivateNotesByUsers, selectPrivateNotesTransactions } from 'app/selectors/ugc/PrivateNotesSelector';
import {
  selectContentAdvisoriesTransactions,
  selectContentAdvisoriesByUsers
} from 'app/selectors/ugc/ContentAdvisoriesSelector';
import {
  selectAgeSuitabilitiesByUsers,
  selectAgeSuitabilitiesTransactions
} from 'app/selectors/ugc/AgeSuitabilitiesSelector';
import { selectSummariesByUsers, selectSummariesTransactions } from 'app/selectors/ugc/SummariesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectHiddenNotices } from 'app/selectors/NoticeSelector';

import accountShape from 'app/shapes/accountShape';

import {
  selectActiveShelfFilters,
  selectShelfOwner,
  selectRequestedShelf,
  selectShelvesSummary,
  selectImportResult,
  selectShelfTransactions,
  selectShelvesForCurrentUser
} from 'app/selectors/ShelfSelector';

import AvailabilityDetailActions from 'app/actions/AvailabilityDetailActions';
import CommentsActions from 'app/actions/CommentsActions';
import DrawerActions from 'app/actions/DrawerActions';
import QuotationsActions from 'app/actions/QuotationsActions';
import TagsActions from 'app/actions/TagsActions';
import VideosActions from 'app/actions/VideosActions';
import IOwnThisActions from 'app/actions/IOwnThisActions';
import NoticeActions from 'app/actions/NoticeActions';
import SimilarTitlesActions from 'app/actions/SimilarTitlesActions';
import PrivateNotesActions from 'app/actions/PrivateNotesActions';
import ContentAdvisoriesActions from 'app/actions/ContentAdvisoriesActions';
import AgeSuitabilitiesActions from 'app/actions/AgeSuitabilitiesActions';
import SummariesActions from 'app/actions/SummariesActions';
import ShelfActions from 'app/actions/ShelfActions';
import WorkflowActions from 'app/actions/WorkflowActions';
import ModalActions from 'app/actions/ModalActions';
import { trackHoldingsView, trackContentCollectionMove } from 'app/actions/AnalyticsGa4Actions';
import Shelf from './Shelf';

export class ShelfContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  getPrintUrl() {
    const { router, owner } = this.props;
    return `/v2/print/users/${owner.get('id')}/shelves/${router.params.shelfName}${router.location.search}`;
  }

  handleAvailabilityLinkClick({ bib, linkText }) {
    const bibId = bib.get('id');
    const ui = {
      ui_container_type: 'item',
      ui_component_type: 'link-text',
      ui_component_label: linkText,
      ui_content_layout: 'medium'
    };

    this.props.trackHoldingsView({ bibId, ui });
  }

  trackContentCollectionMove({ bibId, newShelf, privateItem }) {
    const properties = {
      content_collection_type: `shelf_${newShelf}`,
      privacy_status: privateItem ? 'private' : 'public'
    };
    this.props.trackContentCollectionMove({ bibId, properties });
  }

  render() {
    const {
      activeFilters,
      currentAccount,
      currentLibrary,
      drawer,
      intl,
      isFetching,
      owner,
      shelf,
      summary,
      bibs,
      commentsTransactions,
      quotationsTransactions,
      tagsTransactions,
      videosTransactions,
      iOwnThisTransactions,
      similarTitlesTransactions,
      privateNotesTransactions,
      contentAdvisoriesTransactions,
      ageSuitabilitiesTransactions,
      summariesTransactions,
      shelfTransactions,
      localBranch,
      importResult,
      ownerComments,
      ownerQuotations,
      ownerRatings,
      ownerTags,
      ownerVideos,
      iOwnThisSettings,
      ownerSimilarTitles,
      ownerPrivateNotes,
      ownerContentAdvisories,
      ownerAgeSuitabilities,
      ownerSummaries,
      hiddenNotices,
      currentUserShelves
    } = this.props;

    // Actions
    const {
      availabilityDetailActions,
      commentsActions,
      drawerActions,
      quotationsActions,
      tagsActions,
      videosActions,
      iOwnThisActions,
      noticeActions,
      similarTitlesActions,
      privateNotesActions,
      contentAdvisoriesActions,
      ageSuitabilitiesActions,
      summariesActions,
      shelfActions,
      workflowActions,
      modalActions
    } = this.props;

    const pageTitleId = `section_title_users_${shelf.get('name')}_shelf`;
    const pageTitle = intl.formatMessage({ id: pageTitleId }, { username: owner.get('name') });

    return (
      <div>
        <DocumentHeadContainer pageTitle={pageTitle} />
        <BackToLinkManager pageId={`${shelf.get('name')}_shelf`} />
        <Shelf
          activeFilters={activeFilters}
          currentAccount={currentAccount}
          currentLibrary={currentLibrary}
          drawer={drawer}
          isFetching={isFetching}
          owner={owner}
          pageTitle={pageTitle}
          printURL={this.getPrintUrl()}
          shelf={shelf}
          summary={summary}
          bibs={bibs}
          commentsTransactions={commentsTransactions}
          quotationsTransactions={quotationsTransactions}
          tagsTransactions={tagsTransactions}
          videosTransactions={videosTransactions}
          iOwnThisTransactions={iOwnThisTransactions}
          shelfTransactions={shelfTransactions}
          similarTitlesTransactions={similarTitlesTransactions}
          privateNotesTransactions={privateNotesTransactions}
          contentAdvisoriesTransactions={contentAdvisoriesTransactions}
          ageSuitabilitiesTransactions={ageSuitabilitiesTransactions}
          summariesTransactions={summariesTransactions}
          localBranch={localBranch}
          importResult={importResult}
          ownerComments={ownerComments}
          ownerQuotations={ownerQuotations}
          ownerRatings={ownerRatings}
          ownerTags={ownerTags}
          ownerVideos={ownerVideos}
          iOwnThisSettings={iOwnThisSettings}
          ownerSimilarTitles={ownerSimilarTitles}
          ownerPrivateNotes={ownerPrivateNotes}
          ownerContentAdvisories={ownerContentAdvisories}
          ownerAgeSuitabilities={ownerAgeSuitabilities}
          ownerSummaries={ownerSummaries}
          hiddenNotices={hiddenNotices}
          availabilityDetailActions={availabilityDetailActions}
          commentsActions={commentsActions}
          quotationsActions={quotationsActions}
          drawerActions={drawerActions}
          tagsActions={tagsActions}
          videosActions={videosActions}
          iOwnThisActions={iOwnThisActions}
          noticeActions={noticeActions}
          similarTitlesActions={similarTitlesActions}
          privateNotesActions={privateNotesActions}
          contentAdvisoriesActions={contentAdvisoriesActions}
          ageSuitabilitiesActions={ageSuitabilitiesActions}
          summariesActions={summariesActions}
          shelfActions={shelfActions}
          workflowActions={workflowActions}
          currentUserShelves={currentUserShelves}
          modalActions={modalActions}
          handleAvailabilityLinkClick={this.handleAvailabilityLinkClick}
          trackContentCollectionMove={this.trackContentCollectionMove}
        />
      </div>
    );
  }
}

const drawerShape = ImmutablePropTypes.contains({
  open: PropTypes.bool
});

const importResultShape = ImmutablePropTypes.mapContains({
  count: PropTypes.number,
  status: PropTypes.string
});

ShelfContainer.propTypes = {
  currentAccount: accountShape,
  importResult: importResultShape,
  localBranch: PropTypes.string,
  currentLibrary: ImmutablePropTypes.map.isRequired,
  drawer: drawerShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  owner: userIdentityShape.isRequired,
  shelf: shelfShape.isRequired,
  activeFilters: ImmutablePropTypes.map.isRequired,
  summary: ImmutablePropTypes.map.isRequired,
  bibs: ImmutablePropTypes.map.isRequired,
  ownerRatings: ImmutablePropTypes.map.isRequired,
  ownerComments: ImmutablePropTypes.map.isRequired,
  ownerQuotations: ImmutablePropTypes.map.isRequired,
  ownerTags: ImmutablePropTypes.map.isRequired,
  ownerVideos: ImmutablePropTypes.map.isRequired,
  iOwnThisSettings: ImmutablePropTypes.map.isRequired,
  ownerSimilarTitles: ImmutablePropTypes.map.isRequired,
  ownerPrivateNotes: ImmutablePropTypes.map.isRequired,
  ownerContentAdvisories: ImmutablePropTypes.map.isRequired,
  ownerAgeSuitabilities: ImmutablePropTypes.map.isRequired,
  ownerSummaries: ImmutablePropTypes.map.isRequired,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  iOwnThisTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired,
  ageSuitabilitiesTransactions: ImmutablePropTypes.map.isRequired,
  summariesTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  hiddenNotices: ImmutablePropTypes.set.isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ageSuitabilitiesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  summariesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  noticeActions: PropTypes.objectOf(PropTypes.func).isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  workflowActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentUserShelves: ImmutablePropTypes.map.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func),
  router: routerShape.isRequired,
  trackHoldingsView: PropTypes.func,
  trackContentCollectionMove: PropTypes.func
};

const mapStateToProps = state => {
  const owner = selectShelfOwner(state);
  const ownerId = owner.get('id');

  return {
    owner,
    currentUserShelves: selectShelvesForCurrentUser(state),
    activeFilters: selectActiveShelfFilters(state),
    currentAccount: selectCurrentAccount(state),
    currentLibrary: selectCurrentLibrary(state),
    currentUser: selectCurrentUser(state),
    drawer: state.get('drawer'),
    bibs: state.getIn(['entities', 'bibs'], Immutable.Map()),
    isFetching: state.getIn(['shelves', 'shelfSearch', 'isFetching']),
    localBranch: state.getIn(['app', 'localBranch']),
    shelf: selectRequestedShelf(state),
    summary: selectShelvesSummary(state),
    ownerSimilarTitles: selectSimilarTitlesByUsers(state).get(ownerId, Immutable.Map()),
    ownerComments: selectCommentsByUsers(state).get(ownerId, Immutable.Map()),
    ownerQuotations: selectQuotationsByUsers(state).get(ownerId, Immutable.Map()),
    ownerRatings: selectRatingsByUsers(state).get(ownerId, Immutable.Map()),
    ownerTags: selectTagsByUsers(state).get(ownerId, Immutable.Map()),
    ownerVideos: selectVideosByUsers(state).get(ownerId, Immutable.Map()),
    ownerPrivateNotes: selectPrivateNotesByUsers(state).get(ownerId, Immutable.Map()),
    ownerContentAdvisories: selectContentAdvisoriesByUsers(state).get(ownerId, Immutable.Map()),
    ownerAgeSuitabilities: selectAgeSuitabilitiesByUsers(state).get(ownerId, Immutable.Map()),
    ownerSummaries: selectSummariesByUsers(state).get(ownerId, Immutable.Map()),
    iOwnThisSettings: selectIOwnThisByUsers(state).get(ownerId, Immutable.Map()),
    commentsTransactions: selectCommentsTransactions(state),
    quotationsTransactions: selectQuotationsTransactions(state),
    tagsTransactions: selectTagsTransactions(state),
    videosTransactions: selectVideosTransactions(state),
    shelfTransactions: selectShelfTransactions(state),
    iOwnThisTransactions: selectIOwnThisTransactions(state),
    similarTitlesTransactions: selectSimilarTitlesTransactions(state),
    privateNotesTransactions: selectPrivateNotesTransactions(state),
    contentAdvisoriesTransactions: selectContentAdvisoriesTransactions(state),
    ageSuitabilitiesTransactions: selectAgeSuitabilitiesTransactions(state),
    summariesTransactions: selectSummariesTransactions(state),
    importResult: selectImportResult(state),
    hiddenNotices: selectHiddenNotices(state)
  };
};

const mapDispatchToProps = dispatch => ({
  availabilityDetailActions: bindActionCreators(AvailabilityDetailActions, dispatch),
  commentsActions: bindActionCreators(CommentsActions, dispatch),
  quotationsActions: bindActionCreators(QuotationsActions, dispatch),
  videosActions: bindActionCreators(VideosActions, dispatch),
  iOwnThisActions: bindActionCreators(IOwnThisActions, dispatch),
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  tagsActions: bindActionCreators(TagsActions, dispatch),
  noticeActions: bindActionCreators(NoticeActions, dispatch),
  shelfActions: bindActionCreators(ShelfActions, dispatch),
  similarTitlesActions: bindActionCreators(SimilarTitlesActions, dispatch),
  privateNotesActions: bindActionCreators(PrivateNotesActions, dispatch),
  contentAdvisoriesActions: bindActionCreators(ContentAdvisoriesActions, dispatch),
  ageSuitabilitiesActions: bindActionCreators(AgeSuitabilitiesActions, dispatch),
  summariesActions: bindActionCreators(SummariesActions, dispatch),
  workflowActions: bindActionCreators(WorkflowActions, dispatch),
  modalActions: bindActionCreators(ModalActions, dispatch),
  trackHoldingsView: bindActionCreators(trackHoldingsView, dispatch),
  trackContentCollectionMove: bindActionCreators(trackContentCollectionMove, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ShelfContainer)));
